/**
 * Procedural pixel art.
 *
 * Every sprite is string-art + a palette, rasterised at boot. Two layers are
 * produced per sprite: a "base" layer (fixed colours) and a "mask" layer
 * (player-coloured pixels drawn in white/grey) which is tinted with the owner's
 * colour at draw time. Zero binary assets, and every unit is automatically
 * available in 100 different kingdom colours.
 *
 * **The drawings themselves live in `render/art/`, split by people.** They were
 * redrawn at the doubled tile resolution one race at a time, and that is how
 * they will be revised — by somebody looking at a whole roster together, which
 * is the only way "a dwarf hall must not look like an elf hall" can actually be
 * judged. This file owns the palette, the rasteriser, and the map from those
 * files into the flat arrays the renderer indexes by `UnitType`/`StructureType`.
 */

import { Texture } from 'pixi.js';
import { UnitType } from '../../shared/constants';
import { ART_UNITS_HUMAN } from './art/units-human';
import { ART_UNITS_DWARF } from './art/units-dwarf';
import { ART_UNITS_ELF } from './art/units-elf';
import { ART_UNITS_TRADESHIP, ART_UNITS_WILD } from './art/units-wild';
import { ART_UNITS_WARSHIP } from './art/units-ships';
import { ART_BUILDINGS_COMMON } from './art/buildings-common';
import { ART_BUILDINGS_RACIAL } from './art/buildings-racial';
import { ART_RACE_PORTRAITS, PORTRAIT_PLATE, PORTRAIT_TINT } from './art/portraits';
import { ART_FRAME, FRAME_SLICE, FRAME_TONES, FRAME_W } from './art/frames';

/**
 * The whole colour vocabulary, shared by every sprite.
 *
 * Deliberately small. A fixed palette is what stops fifty separately-drawn
 * sprites from looking like fifty separate games: stone is the same stone in a
 * quarry, a keep and a dwarven hall because there is only one key for it.
 */
const PAL: Record<string, string> = {
  '#': '#151019', // outline
  'd': '#5a6070', // dark metal
  'm': '#c3c9d6', // metal
  'k': '#0d0b12', // eye / visor
  's': '#e8b98a', // skin
  'w': '#7a5230', // wood
  'y': '#ffd85e', // gold / flame
  'r': '#ff6b35', // fire
  'c': '#7ad7ff', // magic
  'v': '#b887ff', // arcane
  'g': '#4f9e4a', // foliage
  'G': '#2c6330', // dark foliage
  'b': '#3a2a1e', // dark wood
  'n': '#8d8574', // stone
  'N': '#c9c2b0', // light stone
  'o': '#241c2c', // shadow
  'e': '#7ba055', // orc skin
  'E': '#4c6a36', // orc shadow
  'i': '#c2cf7e', // goblin skin
  'I': '#8b9a48', // goblin shadow
  'q': '#6b5138', // leather / timber
  'x': '#a83a2c', // war paint
  'a': '#5fa9a0', // murloc hide
  'A': '#3c7a74', // murloc shadow
};

/**
 * Bastions: what a Watchtower becomes when it is raised **on a wall**.
 *
 * A tower standing behind a rampart and a tower built into one are not the same
 * building, and they should not look the same. These are indexed by `Race`,
 * because a wall is the most visible thing a kingdom puts on the map and the
 * gatehouse on it should say at a glance whose border you are looking at.
 *
 * Still on the old ten-pixel grid — they were not part of the double-resolution
 * redraw — so they are rasterised at 2× to sit correctly beside everything
 * else. See `rasterize`.
 */
export const WALL_TOWER_ART: string[][] = [
  /* Human — quarried limestone, battlements, arrow slit */
  [
    '          ',
    '  N N N N ',
    '  NNNNNNN ',
    '  NnPPnnN ',
    '  Nn##nnN ',
    '  NnnnnnN ',
    '  NnPPnnN ',
    '  NNNNNNN ',
    '  nnnnnnn ',
    '          ',
  ],
  /* Dwarf — squat granite blockhouse seamed with lit runes */
  [
    '          ',
    '  d d d d ',
    '  ddddddd ',
    '  dnnnnnd ',
    '  dncPcnd ',
    '  dnPPPnd ',
    '  dncPcnd ',
    '  ddddddd ',
    '  nnnnnnn ',
    '          ',
  ],
  /* Elf — a watch-spire grown out of the palisade itself */
  [
    '   g  g   ',
    '  ggggggg ',
    '   gqqqg  ',
    '   qPPq   ',
    '   qqqq   ',
    '   qPPq   ',
    '   qqqq   ',
    '  gqqqqg  ',
    '  ggggggg ',
    '          ',
  ],
];

/* ------------------------------------------------------------- rasterizer */

function makeCanvas(w: number, h: number) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

/**
 * One character of string-art becomes `k` texture pixels a side.
 *
 * Every unit and building is drawn on the doubled grid — 16 × 16 for a soldier,
 * 24 × 24 for a hall — so they rasterise one to one and `k` is 1.
 *
 * The escape hatch is for art still on the old grid. `WALL_TOWER_ART` is the
 * last of it. Blowing those up two to one keeps them exactly the size they
 * always were beside everything else, which is the point: nothing is ever
 * mid-way broken, the art simply gets sharper as each piece lands.
 */
function rasterize(art: string[], maskLayer: boolean, k = 1): HTMLCanvasElement {
  const h = art.length;
  const w = art[0].length;
  const canvas = makeCanvas(w * k, h * k);
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(w * k, h * k);
  const data = img.data;
  const stride = w * k;

  for (let y = 0; y < h; y++) {
    const row = art[y];
    for (let x = 0; x < w; x++) {
      const ch = row[x] || ' ';
      if (ch === ' ') continue;
      let hex: string | undefined;
      if (ch === 'P' || ch === 'p') {
        if (!maskLayer) continue;
        hex = ch === 'P' ? '#ffffff' : '#a6a6a6';
      } else {
        if (maskLayer) continue;
        hex = PAL[ch];
      }
      if (!hex) continue;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      for (let dy = 0; dy < k; dy++) {
        for (let dx = 0; dx < k; dx++) {
          const o = ((y * k + dy) * stride + (x * k + dx)) * 4;
          data[o] = r; data[o + 1] = g; data[o + 2] = b; data[o + 3] = 255;
        }
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function texFrom(canvas: HTMLCanvasElement): Texture {
  const tex = Texture.from(canvas);
  try { (tex.source as any).scaleMode = 'nearest'; } catch { /* older pixi */ }
  return tex;
}

/**
 * **The art, assembled into enum order.**
 *
 * The drawings are grouped by people; the renderer wants a flat array indexed
 * by `UnitType` / `StructureType`. The two orders are reconciled here, once.
 *
 * Written as an index map rather than concatenation on purpose: the groups do
 * *not* run in enum order — the caravan and the transport sit between the human
 * and dwarf rosters, and the Warehouse was appended after the monster camps. A
 * silent off-by-one here would surface as knights riding wyverns rather than as
 * an error, so every index is stated out loud.
 */
function assemble(size: number, parts: [number, string[][][]][]): string[][][] {
  const out: string[][][] = new Array(size);
  for (const [start, group] of parts) {
    group.forEach((frames, i) => { out[start + i] = frames; });
  }
  return out;
}

/*
 * **The burglars are gone, and these tables had to be re-cut by hand.**
 *
 * Each people's art list still has its rogue in the middle of it — the Thief
 * third in the human roster, the Sapper third among the dwarves, the
 * Shadowdancer third among the elves — because the drawings are harmless and
 * deleting a row from three art files is a bigger change than skipping it here.
 * What matters is that `UnitType` no longer has those three, so every entry
 * after each of them moved down by one. TypeScript cannot catch a mistake in
 * this file: a wrong index is not an error, it is a knight drawn as a wyvern.
 *
 * UnitType now: 0-3 human · 4-5 civilian · 6-9 dwarf · 10-13 elf · 14-19 wild ·
 * 20 merchantman · 21 warship.
 *
 * The warship's entry is the **human** hull, which is what anything asking for
 * a sprite by type alone gets — the menu icon, and any owner whose race is
 * unknown. The map asks a different question: see `warshipFrames`.
 */
const noRogue = (roster: string[][][]) => roster.filter((_, i) => i !== 3);

const UNIT_FRAMES: string[][][] = assemble(22, [
  [0, noRogue(ART_UNITS_HUMAN)],        // Knight, Archer, Wizard, Dragon
  [4, ART_UNITS_WILD.slice(0, 2)],      // Caravan, Transport
  [6, noRogue(ART_UNITS_DWARF)],        // Shieldbearer, Crossbowman, Runesmith, Golem
  [10, noRogue(ART_UNITS_ELF)],         // Blademaster, Longbow, Druid, Phoenix
  [14, ART_UNITS_WILD.slice(2)],        // Orc … Griffin
  [20, [ART_UNITS_TRADESHIP]],          // Merchantman
  [21, [ART_UNITS_WARSHIP[0]]],         // Warship — the human hull as the default
]);

/*
 * StructureType: 0-8 shared · 9-22 racial and camps · 23 Warehouse.
 *
 * Three halls came out of the racial list — the Thieves' Den at 1, the Deep
 * Tunnel at 5 and the Shadow Glade at 9, counting within that list — so the
 * survivors are picked out by index rather than sliced.
 */
const RACIAL_KEPT = [0, 2, 3, 4, 6, 7, 8, 10, 11, 12, 13, 14, 15, 16];

const STRUCT_FRAMES: string[][][] = assemble(24, [
  [0, ART_BUILDINGS_COMMON.slice(0, 9)],                  // Keep … Harbour
  [9, RACIAL_KEPT.map(i => ART_BUILDINGS_RACIAL[i])],     // Barracks … Griffin Nest
  [23, ART_BUILDINGS_COMMON.slice(9)],                    // Warehouse
]);

/**
 * **The same drawing, as a picture for the interface.**
 *
 * The build palette and the right-click wheel used to show a separate set of
 * hand-drawn glyphs, which meant the picture on the button and the thing that
 * appeared on the map were two different drawings that could — and did — drift
 * apart. Now the button shows the building.
 *
 * Player-coloured pixels are painted in a neutral stone tone rather than left
 * out: a menu entry belongs to nobody, and a knight with a hole where his
 * tabard should be reads as a broken sprite.
 */
const MENU_TINT = '#b9c2d4';
const MENU_TINT_DARK = '#8b93a4';
const iconCache: Record<string, string> = {};

function rasterizeIcon(art: string[]): string {
  const h = art.length;
  const w = art[0].length;
  const canvas = makeCanvas(w, h);
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const data = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ch = art[y][x] || ' ';
      if (ch === ' ') continue;
      const hex = ch === 'P' ? MENU_TINT : ch === 'p' ? MENU_TINT_DARK : PAL[ch];
      if (!hex) continue;
      const o = (y * w + x) * 4;
      data[o] = parseInt(hex.slice(1, 3), 16);
      data[o + 1] = parseInt(hex.slice(3, 5), 16);
      data[o + 2] = parseInt(hex.slice(5, 7), 16);
      data[o + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL();
}

/**
 * **The browser tab icon, drawn from the game's own art.**
 *
 * It used to be the ⚔ codepoint in an inline SVG, which the browser rendered in
 * the page's text colour — near-black, on a tab strip that is also dark. The
 * result was a tab you could not pick out of a row of tabs, which is the one
 * job a favicon has.
 *
 * So it is the Knight, the same character-grid drawing that walks around the
 * map, rasterised on a warm brown field. The field is the important half: a
 * silhouette alone disappears against whichever tab-bar colour the browser
 * happens to use, while a filled brown tile reads against both a light and a
 * dark one. Player-coloured pixels are painted in pale gold rather than the
 * menu's cool stone, because the icon is 16 pixels across on most screens and
 * needs the contrast far more than it needs the exact tabard colour.
 *
 * Generated at boot like everything else here — no icon file, nothing to keep
 * in step with the sprite it is supposed to be a picture of.
 */
export function faviconUrl(size = 64): string {
  const art = UNIT_FRAMES[UnitType.Knight]?.[0];
  return art ? onPlate(art, size, '#6b4a2a', '#f0d9a0', '#c2a468') : '';
}

/**
 * **The face on a race card.**
 *
 * Same idea as the favicon and the same machinery, but the drawing is a bust
 * from `art/portraits.ts` rather than a map sprite — see the note at the top of
 * that file for why a unit seen from above makes a poor portrait.
 *
 * Cached by race and size: the picker is rebuilt on every edition switch and on
 * every pick, and re-rasterising three canvases each time would be work done
 * for nothing.
 */
export function racePortraitUrl(race: number, size = 96): string {
  const key = `p${race}@${size}`;
  if (iconCache[key] !== undefined) return iconCache[key];
  const art = ART_RACE_PORTRAITS[race];
  iconCache[key] = art
    ? onPlate(art, size, PORTRAIT_PLATE[race] || '#4a4238', PORTRAIT_TINT[race] || '#e6d5a8')
    : '';
  return iconCache[key];
}

/**
 * **The panel frame for a people**, as a `border-image` source.
 *
 * Returns the data URL and the slice size *in rendered pixels*, because the
 * caller has to write both into CSS and getting them out of step is the one way
 * this can look wrong — a slice that does not match the drawing smears the
 * corners across the edges.
 *
 * Drawn at whole-pixel scale, like everything else here: `border-image` will
 * happily resample, and a resampled pixel frame is a blurred one.
 */
export function frameUrl(race: number, scale = 3): { url: string; slice: number } {
  const key = `f${race}@${scale}`;
  const tones = FRAME_TONES[race] || FRAME_TONES[0];
  if (iconCache[key] === undefined) {
    const px = FRAME_W * scale;
    const canvas = makeCanvas(px, px);
    const ctx = canvas.getContext('2d')!;
    const ink: Record<string, string> = {
      '#': tones[0], 'd': tones[1], 'm': tones[2], 'y': tones[3],
    };
    for (let y = 0; y < FRAME_W; y++) {
      const row = ART_FRAME[y];
      for (let x = 0; x < FRAME_W; x++) {
        const hex = ink[row[x] || ' '];
        if (!hex) continue;
        ctx.fillStyle = hex;
        ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
    iconCache[key] = canvas.toDataURL();
  }
  return { url: iconCache[key], slice: FRAME_SLICE * scale };
}

/**
 * Draw character-grid art centred on a rounded field, and hand back a data URL.
 *
 * The field is the half that matters. A silhouette alone disappears into
 * whatever is behind it — a browser's tab strip, a menu card, a Discord avatar
 * ring — while a filled tile reads against all of them. `tint`/`tintDark` are
 * what the player-coloured pixels become here, since none of these pictures
 * belongs to a kingdom.
 */
function onPlate(art: string[], size: number, plate: string, tint: string, tintDark = tint): string {
  const h = art.length, w = art[0].length;
  const canvas = makeCanvas(size, size);
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = plate;
  const r = size * 0.22;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(size, 0, size, size, r);
  ctx.arcTo(size, size, 0, size, r);
  ctx.arcTo(0, size, 0, 0, r);
  ctx.arcTo(0, 0, size, 0, r);
  ctx.closePath();
  ctx.fill();

  // whole-pixel scaling only: a fractional factor turns pixel art into mush
  const k = Math.max(1, Math.floor((size * 0.86) / Math.max(w, h)));
  const ox = Math.round((size - w * k) / 2);
  const oy = Math.round((size - h * k) / 2);
  for (let y = 0; y < h; y++) {
    const row = art[y];
    for (let x = 0; x < w; x++) {
      const ch = row[x] || ' ';
      if (ch === ' ') continue;
      const hex = ch === 'P' ? tint : ch === 'p' ? tintDark : PAL[ch];
      if (!hex) continue;
      ctx.fillStyle = hex;
      ctx.fillRect(ox + x * k, oy + y * k, k, k);
    }
  }
  return canvas.toDataURL();
}

/** Menu picture for a unit type, or '' if there is no such unit. */
export function unitIconUrl(type: number): string {
  const key = `u${type}`;
  if (iconCache[key] !== undefined) return iconCache[key];
  const frames = UNIT_FRAMES[type];
  iconCache[key] = frames ? rasterizeIcon(frames[0]) : '';
  return iconCache[key];
}

/** Menu picture for a building type, or '' if there is no such building. */
export function structIconUrl(type: number): string {
  const key = `s${type}`;
  if (iconCache[key] !== undefined) return iconCache[key];
  const frames = STRUCT_FRAMES[type];
  iconCache[key] = frames ? rasterizeIcon(frames[0]) : '';
  return iconCache[key];
}

export interface SpriteSet {
  base: Texture[];
  mask: Texture[];
  w: number;
  h: number;
}

export const unitSprites: SpriteSet[] = [];
/**
 * The warship, one hull per people — indexed by `Race`.
 *
 * Kept beside `unitSprites` rather than in it because it is the one unit whose
 * drawing depends on **who owns it** and not only on what it is. Every other
 * racial unit is a different `UnitType`; a dwarven ironclad and an elven cutter
 * are the same ship with the same numbers, so they share a type and differ only
 * here. See `ART_UNITS_WARSHIP`.
 */
export const warshipSprites: SpriteSet[] = [];
export const structSprites: SpriteSet[] = [];
/**
 * Watchtowers built into a wall, one per race.
 *
 * **Nothing reads these today.** The bastion was retired — no building may
 * stand on a wall, from either direction — so the renderer always draws a plain
 * tower (see `entities.ts`). The art and the rasterising are kept rather than
 * deleted because they are the drawing, not the rule: if a tower is ever
 * allowed onto stonework again, this is what it should look like. Said out loud
 * here so the next reader does not go hunting for the consumer.
 */
export const wallTowerSprites: SpriteSet[] = [];
export let particleTex: Texture;
export let ringTex: Texture;
export let arrowTex: Texture;
export let flagTex: Texture;

export function buildSprites() {
  if (unitSprites.length) return;

  /*
   * Frames come from the art now instead of being faked.
   *
   * The old code manufactured a second frame by shifting the bottom two rows of
   * pixels sideways, which read as a sliding statue rather than a walk. Every
   * unit is now drawn with a real mid-stride frame, so the walk cycle is
   * something somebody decided rather than something a loop did to them.
   *
   * `w`/`h` are the rendered size, which the entity layer uses to place
   * selection rings and health bars — read off the raster, not the source grid,
   * so a scaled sprite can never end up with furniture at half its size.
   */
  for (const frames of UNIT_FRAMES) {
    unitSprites.push({
      base: frames.map(f => texFrom(rasterize(f, false))),
      mask: frames.map(f => texFrom(rasterize(f, true))),
      w: frames[0][0].length,
      h: frames[0].length,
    });
  }

  for (const frames of ART_UNITS_WARSHIP) {
    warshipSprites.push({
      base: frames.map(f => texFrom(rasterize(f, false))),
      mask: frames.map(f => texFrom(rasterize(f, true))),
      w: frames[0][0].length,
      h: frames[0].length,
    });
  }

  // buildings are usually one frame; a forge, a mill and moving water are two
  for (const frames of STRUCT_FRAMES) {
    structSprites.push({
      base: frames.map(f => texFrom(rasterize(f, false))),
      mask: frames.map(f => texFrom(rasterize(f, true))),
      w: frames[0][0].length,
      h: frames[0].length,
    });
  }

  // the last of the old-grid art: doubled so it matches everything beside it
  for (const art of WALL_TOWER_ART) {
    wallTowerSprites.push({
      base: [texFrom(rasterize(art, false, 2))],
      mask: [texFrom(rasterize(art, true, 2))],
      w: art[0].length * 2,
      h: art.length * 2,
    });
  }

  {
    const c = makeCanvas(2, 2);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 2, 2);
    particleTex = texFrom(c);
  }
  {
    const size = 12;
    const c = makeCanvas(size, size);
    const ctx = c.getContext('2d')!;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
    ringTex = texFrom(c);
  }
  {
    const c = makeCanvas(4, 1);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 4, 1);
    arrowTex = texFrom(c);
  }
  {
    // the little rally pennant, doubled with everything else
    const art = [
      ' #  ',
      ' PP#',
      ' PP#',
      ' P# ',
      ' #  ',
      ' #  ',
    ];
    flagTex = texFrom(rasterize(art, true, 2));
  }
}
